import ContentLayout from "../components/layout/ContentLayout";
import { Button, Form, Input, message, Select, Space, TimePicker } from "antd";
import LocationPicker from "../components/LocationPicker";
import MyModal from "../components/MyModal";
import { useRef, useState, useEffect } from "react";
import { createOutlet, updateOutlet, getOutlets } from "../actions/acc_actions";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { daysOfWeek } from "../utils/functions";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Loading from "../utils/Loading";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

dayjs.extend(customParseFormat);

export default function Outlets() {
  const { t } = useTranslation();
  const businessUrn = useSelector((state) => state.account.businessUrn);
  const { outletUrn } = useParams();
  const [form] = Form.useForm();
  const modalRef = useRef();
  const [isLoading, setIsLoading] = useState(false);

  const { isLoading: loadingData, data } = useQuery({
    queryKey: ["outlets", businessUrn],
    queryFn: () => getOutlets(businessUrn),
    enabled: !!businessUrn,
  });

  const outlets = data?.data?.body || [];

  // Prefill form if editing
  useEffect(() => {
    if (outletUrn && outlets.length) {
      const outlet = outlets.find((o) => o.urn === outletUrn);
      if (outlet) {
        const opening_times = (outlet.config?.opening_times || []).map(
          (day) => ({
            day,
            time: [dayjs("09:00", "HH:mm"), dayjs("17:00", "HH:mm")],
          })
        );
        form.setFieldsValue({
          name: outlet.name,
          house_number: outlet.address?.house_number,
          street_name: outlet.address?.street_name,
          town: outlet.address?.town,
          postal_code: outlet.address?.postal_code,
          lat: outlet.location?.lat,
          lng: outlet.location?.long,
          opening_times,
        });
      }
    }
  }, [outletUrn, outlets, form]);

  if (loadingData) return <Loading />;

  const handleFinish = (values) => {
    const formattedTimes = values.opening_times
      .map(({ day, time }) => {
        const [start, end] = time;
        return `${day}: ${dayjs(start).format("HH:mm")}-${dayjs(end).format(
          "HH:mm"
        )}`;
      })
      .join(", ");

    const payload = {
      name: values.name,
      address: {
        house_number: values.house_number,
        street_name: values.street_name,
        town: values.town,
        postal_code: values.postal_code,
      },
      location: {
        lat: String(values.lat),
        long: String(values.lng),
      },
      opening_times: formattedTimes,
    };

    setIsLoading(true);

    const request = outletUrn
      ? updateOutlet({ businessUrn, outletUrn, payload })
      : createOutlet(payload, businessUrn);

    request
      .then(() => {
        message.success(
          outletUrn
            ? t("outlet_added_successful")
            : t("outlet_added_successful")
        );
        modalRef.current.showModal();
      })
      .catch((err) => {
        console.error(err);
        message.error(err.response?.data?.message || t("loading"));
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <ContentLayout
      header={
        <>
          <h1>{t("welcome_outlets")}</h1>
          <p className="content_layout-subtitle">{t("add_your_outlets")}</p>
        </>
      }
    >
      <div className="rewards-form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="rewards-form"
        >
          <div className="rewards-form-section">
            <h4>{t("details")}</h4>
            <div className="rewards-inputs">
              <Form.Item
                name="name"
                label={t("outlet_name")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </div>
          </div>

          <div className="rewards-form-section">
            <h4>{t("location")}</h4>
            <div className="rewards-inputs">
              <div className="form-row">
                <Form.Item
                  name="house_number"
                  label={t("house_number")}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="street_name"
                  label={t("street_name")}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </div>
              <Form.Item
                name="town"
                label={t("town")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="postal_code"
                label={t("postal_code")}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <LocationPicker form={form} />
            </div>
          </div>

          <Form.List name="opening_times">
            {(fields, { add, remove }) => (
              <div className="rewards-form-section">
                <h4>{t("opening_times")}</h4>
                <div className="rewards-inputs">
                  {fields.map((field) => (
                    <Space
                      key={field.key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "day"]}
                        rules={[{ required: true, message: t("select_day") }]}
                      >
                        <Select
                          placeholder={t("select_day")}
                          options={daysOfWeek}
                          style={{ width: 120 }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, "time"]}
                        rules={[
                          { required: true, message: t("select_time_range") },
                        ]}
                      >
                        <TimePicker.RangePicker format="HH:mm" />
                      </Form.Item>

                      <Button
                        danger
                        onClick={() => remove(field.name)}
                        type="dashed"
                      >
                        {t("remove")}
                      </Button>
                    </Space>
                  ))}

                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon="+">
                      {t("add_day_time")}
                    </Button>
                  </Form.Item>
                </div>
              </div>
            )}
          </Form.List>

          <Form.Item className="rewards-form-btn">
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading}
              style={{ width: "14rem" }}
            >
              {isLoading ? t("loading") : t("add")}
            </Button>
          </Form.Item>
        </Form>
      </div>

      <MyModal
        ref={modalRef}
        title={t("outlet_added_successful")}
        message={t("outlet_added_message")}
        imageSrc="/assets/images/modal/stamp.png"
        confirmText={t("back_to_outlets")}
        navigatePath="/outlets"
      />
    </ContentLayout>
  );
}
